import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { confirmAlert } from 'react-confirm-alert';
import {
	HiOutlineChevronDown,
	HiOutlinePlus,
} from 'react-icons/hi2';
import { FiHeadphones, FiLock, FiLogOut } from 'react-icons/fi';
import { FaCheckCircle } from 'react-icons/fa';
import claritickLogo from '../../../../css/img/claritick_tm_dark.svg';
import WorkSpaceEntry from '../../../modals/Workspaces/WorkSpaceEntry';
import { addWorkspaceToUser, fetchUser } from '../../../redux/actions';
import { baseURL } from '../../../helpers/baseURL';
import { headers } from '../../../helpers/Requests';
import styles from './LockedWorkspace.module.css';

const renderTicketCard = (ticket, key) => {
	const statusColor = ticket.status?.color || '#6b7280';

	return (
		<div
			key={key}
			className={styles.ticketCard}
			style={{ borderLeftColor: statusColor }}
		>
			<div className={styles.ticketCardTitle}>{ticket.title}</div>
			{ticket.description && (
				<div className={styles.ticketCardDescription}>{ticket.description}</div>
			)}
			<div className={styles.ticketCardMeta}>
				{ticket.status && (
					<span
						className={styles.statusBadge}
						style={{ backgroundColor: statusColor }}
					>
						{ticket.status.name}
					</span>
				)}
				{ticket.department && <span>{ticket.department.name}</span>}
				{ticket.created_at && <span>{ticket.created_at}</span>}
			</div>
		</div>
	);
};

const TicketSlideshow = ({ tickets }) => {
	const trackRef = useRef(null);
	const firstSetRef = useRef(null);
	const viewportRef = useRef(null);
	const [loopTickets, setLoopTickets] = useState([]);
	const [loopReady, setLoopReady] = useState(false);
	const [duration, setDuration] = useState(18);

	useEffect(() => {
		if (!tickets.length) {
			setLoopTickets([]);
			setLoopReady(false);
			return;
		}

		const minMultiplier = Math.max(2, Math.ceil(8 / tickets.length));
		setLoopTickets(Array.from({ length: minMultiplier }, () => tickets).flat());
		setLoopReady(false);
	}, [tickets]);

	useEffect(() => {
		if (!firstSetRef.current || !viewportRef.current || !loopTickets.length) {
			return undefined;
		}

		const measure = () => {
			const firstSet = firstSetRef.current;
			const viewport = viewportRef.current;
			const track = trackRef.current;
			if (!firstSet || !viewport || !track) {
				return;
			}

			const setHeight = firstSet.getBoundingClientRect().height;
			const trackGap = parseFloat(getComputedStyle(track).rowGap || getComputedStyle(track).gap || '0');
			const loopHeight = setHeight + trackGap;
			const viewportHeight = viewport.getBoundingClientRect().height;
			const multiplier = loopTickets.length / tickets.length;

			if (loopHeight < viewportHeight && multiplier < 24) {
				setLoopTickets(Array.from({ length: multiplier + 1 }, () => tickets).flat());
				setLoopReady(false);
				return;
			}

			track.style.setProperty('--loop-height', `${loopHeight}px`);
			setDuration(Math.max(loopTickets.length * 3.5, 18));
			setLoopReady(true);
		};

		const frame = requestAnimationFrame(measure);
		const observer = new ResizeObserver(() => {
			requestAnimationFrame(measure);
		});

		observer.observe(firstSetRef.current);
		observer.observe(viewportRef.current);

		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
		};
	}, [loopTickets, tickets]);

	if (!loopTickets.length) {
		return null;
	}

	return (
		<div className={styles.ticketSlideshow}>
			<div className={styles.ticketSlideshowFadeTop} aria-hidden="true" />
			<div className={styles.ticketSlideshowViewport} ref={viewportRef}>
				<div
					className={`${styles.previewTrack} ${loopReady ? styles.previewTrackActive : ''}`}
					ref={trackRef}
					style={{ '--scroll-duration': `${duration}s` }}
				>
					<div className={styles.previewSet} ref={firstSetRef}>
						{loopTickets.map((ticket, index) => renderTicketCard(ticket, `a-${ticket.id}-${index}`))}
					</div>
					<div className={styles.previewSet} aria-hidden="true">
						{loopTickets.map((ticket, index) => renderTicketCard(ticket, `b-${ticket.id}-${index}`))}
					</div>
				</div>
			</div>
			<div className={styles.ticketSlideshowFadeBottom} aria-hidden="true" />
		</div>
	);
};

const LockedWorkspace = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const user = useSelector((state) => state.userReducer?.user);
	const menuRef = useRef(null);

	const [summary, setSummary] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState('');
	const [menuOpen, setMenuOpen] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [switchingId, setSwitchingId] = useState(null);

	const currentWorkspace = useMemo(() => {
		return user?.workspaces?.find((workspace) => workspace.id === user.current_workspace) || null;
	}, [user]);

	const loadSummary = useCallback(() => {
		setLoading(true);
		setLoadError('');
		axios.get(`${baseURL}workspace/locked-summary`, headers)
			.then((response) => {
				const data = response.data;
				if (!data?.locked) {
					history.replace('/dashboard');
					return;
				}
				setSummary(data);
			})
			.catch((error) => {
				if (error.response?.status === 423) {
					setSummary({
						locked: true,
						workspace: {
							id: currentWorkspace?.id,
							name: currentWorkspace?.name || 'Workspace',
						},
						reason: {
							message: error.response?.data?.message || 'This workspace is locked.',
						},
						summary: { tickets: 0, departments: 0, users: 0, emails: 0 },
						recent_tickets: [],
						can_reactivate: Boolean(user?.isOwner),
					});
					return;
				}
				console.error(error);
				setLoadError('Unable to load workspace details. Please refresh the page.');
			})
			.finally(() => setLoading(false));
	}, [history, currentWorkspace?.id, currentWorkspace?.name, user?.isOwner]);

	useEffect(() => {
		if (!user) {
			dispatch(fetchUser());
		}
	}, [dispatch, user]);

	useEffect(() => {
		if (!user?.current_workspace) {
			return;
		}
		setSummary(null);
		loadSummary();
	}, [user?.current_workspace, loadSummary]);

	useEffect(() => {
		if (!user || loading || !summary) {
			return;
		}
		if (!summary.locked && !user.workspace_locked) {
			history.replace('/dashboard');
		}
	}, [user, loading, summary, history]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleSwitchWorkspace = (workspaceId) => {
		if (!workspaceId || workspaceId === user?.current_workspace || switchingId) {
			return;
		}
		setSwitchingId(workspaceId);
		axios.get(`${baseURL}workspace-switch/${workspaceId}`, headers)
			.then(() => {
				setMenuOpen(false);
				history.push('/switch-workspace');
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => setSwitchingId(null));
	};

	const handleLogout = () => {
		confirmAlert({
			title: 'Sign out',
			message: 'Are you sure you want to sign out?',
			buttons: [
				{
					label: 'Yes',
					onClick: () => {
						axios.post(`${baseURL}logout`, {}, {
							headers: {
								Authorization: `Bearer ${Cookies.get('access_token')}`,
							},
						}).finally(() => {
							Cookies.remove('access_token');
							Cookies.remove('mydev_token');
							localStorage.clear();
							window.location.href = '/';
						});
					},
				},
				{ label: 'No' },
			],
			overlayClassName: 'confirm-overlay',
		});
	};

	const attachNewWorkspace = (workspace) => {
		if (!workspace?.id) {
			return;
		}

		dispatch(addWorkspaceToUser(workspace));
		setShowCreateModal(false);
		setSwitchingId(workspace.id);

		axios.get(`${baseURL}workspace-switch/${workspace.id}`, headers)
			.then(() => {
				setMenuOpen(false);
				history.push('/switch-workspace');
			})
			.catch((error) => {
				console.error(error);
				dispatch(fetchUser());
			})
			.finally(() => setSwitchingId(null));
	};

	if (loading) {
		return (
			<div className={styles.lockedPage}>
				<div className={styles.loadingWrap}>
					<Spinner animation="border" role="status">
						<span className="visually-hidden">Loading...</span>
					</Spinner>
				</div>
			</div>
		);
	}

	if (!summary) {
		return (
			<div className={styles.lockedPage}>
				<div className={styles.loadingWrap}>
					<p>{loadError || 'Unable to load this workspace.'}</p>
					<button type="button" className={styles.reactivateBtn} onClick={loadSummary}>
						Try again
					</button>
				</div>
			</div>
		);
	}

	const ticketCount = summary.summary?.tickets ?? 0;
	const recentTickets = summary.recent_tickets || [];

	return (
		<div className={styles.lockedPage}>
			<header className={styles.topBar}>
				<img src={claritickLogo} alt="Claritick" className={styles.logo} />

				<div className={styles.topBarCenter}>
					<div className={styles.workspaceSwitcher} ref={menuRef}>
						<button
							type="button"
							className={styles.workspaceToggle}
							onClick={() => setMenuOpen((open) => !open)}
							aria-expanded={menuOpen}
						>
							<span className={styles.workspaceName}>
								{currentWorkspace?.name || summary.workspace?.name || 'Workspace'}
							</span>
							<span className={styles.lockedBadge}>
								<FiLock size={11} />
								Locked
							</span>
							<HiOutlineChevronDown size={16} />
						</button>

						{menuOpen && (
							<div className={styles.workspaceMenu}>
								<div className={styles.workspaceMenuHeader}>Switch workspace</div>
								{(user?.workspaces || []).map((workspace) => (
									<button
										key={workspace.id}
										type="button"
										className={`${styles.workspaceOption} ${workspace.id === user.current_workspace ? styles.workspaceOptionCurrent : ''}`}
										onClick={() => handleSwitchWorkspace(workspace.id)}
										disabled={switchingId === workspace.id}
									>
										<div>
											<div className={styles.workspaceOptionName}>{workspace.name}</div>
											<div className={styles.workspaceOptionMeta}>
												{workspace.plan || 'Free'}
												{workspace.locked ? ' · Locked' : ''}
											</div>
										</div>
										{workspace.id === user.current_workspace && (
											workspace.locked ? (
												<FiLock className={styles.workspaceOptionLocked} aria-hidden="true" />
											) : (
												<FaCheckCircle className="text-success" aria-hidden="true" />
											)
										)}
									</button>
								))}
								<div className={styles.workspaceMenuFooter}>
									<button
										type="button"
										className={styles.createWorkspaceBtn}
										onClick={() => {
											setMenuOpen(false);
											setShowCreateModal(true);
										}}
									>
										<HiOutlinePlus size={16} className="me-1" />
										Create new workspace
									</button>
								</div>
							</div>
						)}
					</div>
				</div>

				<button type="button" className={styles.signOutBtn} onClick={handleLogout}>
					<FiLogOut size={16} />
					Sign Out
				</button>
			</header>

			<div className={styles.content}>
				<div className={styles.leftPanel}>
					<div className={styles.leftPanelInner}>
						<div className={styles.introBlock}>
							{summary.workspace?.locked_at && (
								<div className={styles.lockedEyebrow}>
									<FiLock size={13} aria-hidden="true" />
									Locked since {summary.workspace.locked_at}
								</div>
							)}

							<h1 className={styles.headline}>
								Unlock access and{' '}
								<span className={styles.headlineAccent}>keep supporting your customers</span>
							</h1>
						</div>

						<div className={styles.heroStat}>
							<span className={styles.heroStatNumber}>{ticketCount}</span>
							<span className={styles.heroStatLabel}>
								{ticketCount === 1 ? 'ticket' : 'tickets'} in{' '}
								<strong>{summary.workspace?.name}</strong>
							</span>
						</div>

						<div className={styles.prose}>
							<p>
								<strong>{summary.workspace?.name}</strong> is currently locked
								{summary.workspace?.locked_at ? ` as of ${summary.workspace.locked_at}` : ''}.
								{' '}
								{summary.reason?.detail || summary.reason?.message || 'Your subscription is no longer active.'}
							</p>
							{(summary.reason?.action || !summary.can_reactivate) && (
								<p className={styles.proseHighlight}>
									{summary.reason?.action || 'Contact your workspace owner to reactivate, or switch to another workspace above.'}
								</p>
							)}
						</div>

						<div className={styles.actions}>
							{summary.can_reactivate ? (
								<Link to="/subscription/plan" className={styles.reactivateBtn}>
									<FaCheckCircle size={18} aria-hidden="true" />
									Re-Activate
								</Link>
							) : (
								<span className={styles.supportBtnMuted}>
									Ask your workspace owner to reactivate
								</span>
							)}
							<a href="mailto:info@claritick.com" className={styles.supportBtn}>
								<FiHeadphones size={18} />
								Contact Support
							</a>
						</div>
					</div>
				</div>

				<div className={styles.rightPanel}>
					{recentTickets.length ? (
						<TicketSlideshow tickets={recentTickets} />
					) : (
						<div className={styles.emptyPreview}>
							Your tickets and conversations are waiting here once you reactivate.
						</div>
					)}
				</div>
			</div>

			{showCreateModal && (
				<WorkSpaceEntry
					showModal={showCreateModal}
					closeModal={() => setShowCreateModal(false)}
					onAdd={attachNewWorkspace}
					onEdit={() => dispatch(fetchUser())}
					isEditing={false}
					item={{}}
				/>
			)}
		</div>
	);
};

export default LockedWorkspace;
